import React, { useState, memo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faXmark,
  faCode,
  faPalette,
  faTools,
  faChevronDown,
  faChevronUp
} from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import Img from "../../components/img";
import useSEO from "../../hooks/useSEO";

// Icons
import htmlIcon from "../../assets/icons/html.svg";
import cssIcon from "../../assets/icons/css.svg";
import javascriptIcon from "../../assets/icons/javascript.svg";
import typescriptIcon from "../../assets/icons/typescript.svg";
import pythonIcon from "../../assets/icons/python.svg";
import tailwindIcon from "../../assets/icons/tailwind.svg";
import reactIcon from "../../assets/icons/react.svg";
import nodejsIcon from "../../assets/icons/nodejs-dark.svg";
import mongodbIcon from "../../assets/icons/mongodb.svg";
import expressIcon from "../../assets/icons/express.svg";
import nextjsIcon from "../../assets/icons/nextjs.svg";
import postgresqlIcon from "../../assets/icons/postgresql.svg";
import dockerIcon from "../../assets/icons/docker.svg";
import vscodeIcon from "../../assets/icons/vscode.svg";
import githubIcon from "../../assets/icons/github.svg";
import notepadppIcon from "../../assets/icons/notepadplusplus.svg";

import { pageVariants, sectionVariants, containerVariants, itemVariants } from "../../config/animations";

// ========== SKILL CARD ==========
const SkillCard = memo(function SkillCard({ skill, searchQuery }) {
  const percentage = (skill.level / 5) * 100;

  const highlightText = (text, query) => {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, "gi");
    return text.split(regex).map((part, i) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <span key={i} className="text-slate-900 font-bold bg-yellow-200 px-0.5 rounded">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const getLevelText = (level) => {
    const levels = ["初学", "入门", "熟练", "精通", "专家"];
    return levels[level - 1] || "未知";
  };

  return (
    <motion.div
      variants={itemVariants}
      className="p-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300"
    >
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="size-12 min-w-12 rounded-xl overflow-hidden bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
          <Img className="size-8" src={skill.img} alt={skill.name} />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="text-base font-bold text-slate-800 dark:text-white truncate">
              {highlightText(skill.name, searchQuery)}
            </h3>
            <span className="text-xs font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-full whitespace-nowrap">
              {percentage}%
            </span>
          </div>

          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">
            {highlightText(skill.description, searchQuery)}
          </p>

          {/* Progress bar */}
          <div className="mt-3">
            <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${percentage}%` }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="bg-slate-700 dark:bg-slate-400 h-2 rounded-full"
              />
            </div>
            <div className="flex justify-between mt-1">
              <span className="text-xs text-slate-400">{getLevelText(skill.level)}</span>
              <span className="text-xs text-slate-400">Lv.{skill.level}/5</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
});

// ========== MAIN ==========
function Skills() {
  useSEO({
    title: "Skills",
    description: "探索我的技术栈：HTML, CSS, JavaScript, React, Node.js等编程语言和框架。",
    keywords: "skills, programming, react, javascript, python, developer skills",
    url: "https://wangyi.is-a.dev/skills"
  });

  const [openCategory, setOpenCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  const toggleCategory = (index) => {
    setOpenCategory(openCategory === index ? null : index);
  };

  const skillList = [
    { name: "HTML", description: "网页的核心构建块，定义结构和语义内容。", img: htmlIcon, level: 4, category: "lang" },
    { name: "CSS", description: "用于设计现代响应式美观界面的样式语言。", img: cssIcon, level: 4, category: "lang" },
    { name: "JavaScript", description: "实现动态、交互式网页体验的多用途语言。", img: javascriptIcon, level: 3, category: "lang" },
    { name: "TypeScript", description: "JavaScript的超集，提供静态类型，提高可维护性。", img: typescriptIcon, level: 3, category: "lang" },
    { name: "Python", description: "适合AI、机器学习、数据科学和后端开发的高级语言。", img: pythonIcon, level: 3, category: "lang" },
    { name: "MongoDB", description: "用于可扩展性和灵活性的NoSQL数据库，支持JSON文档。", img: mongodbIcon, level: 3, category: "framework" },
    { name: "PostgreSQL", description: "高级开源SQL数据库，可靠性和性能强大。", img: postgresqlIcon, level: 3, category: "framework" },
    { name: "ExpressJS", description: "轻量级Node.js框架，用于API和服务器端应用。", img: expressIcon, level: 3, category: "framework" },
    { name: "ReactJS", description: "用于构建快速、基于组件的用户界面的流行JS库。", img: reactIcon, level: 4, category: "framework" },
    { name: "Next.js", description: "支持SSR和全栈应用的React框架，性能优化。", img: nextjsIcon, level: 3, category: "framework" },
    { name: "Tailwind CSS", description: "用于快速UI开发的实用CSS框架。", img: tailwindIcon, level: 4, category: "framework" },
    { name: "Node.js", description: "用于可扩展事件驱动web应用的服务器端运行环境。", img: nodejsIcon, level: 3, category: "framework" },
    { name: "Docker", description: "容器化工具，确保部署环境一致。", img: dockerIcon, level: 3, category: "tool" },
    { name: "Visual Studio Code", description: "轻量强大的代码编辑器，支持丰富插件。", img: vscodeIcon, level: 5, category: "tool" },
    { name: "GitHub", description: "开发者的版本控制和协作平台。", img: githubIcon, level: 4, category: "tool" },
    { name: "Notepad++", description: "简单、快速、轻量的文本编辑器。", img: notepadppIcon, level: 4, category: "tool" },
  ];

  const categories = [
    { icon: "💻", name: "编程语言", filter: "lang", contents: skillList.filter(s => s.category === "lang") },
    { icon: "🎨", name: "框架与库", filter: "framework", contents: skillList.filter(s => s.category === "framework") },
    { icon: "🛠️", name: "工具与平台", filter: "tool", contents: skillList.filter(s => s.category === "tool") },
  ];

  // Filter by search and active filter
  const filteredCategories = categories.map((category) => ({
    ...category,
    contents: category.contents.filter(
      (skill) =>
        (activeFilter === "all" || skill.category === activeFilter) &&
        (skill.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          skill.description.toLowerCase().includes(searchQuery.toLowerCase()))
    ),
  })).filter(cat => activeFilter === "all" || cat.filter === activeFilter);

  const filterButtons = [
    { id: "all", label: "全部", icon: null },
    { id: "lang", label: "语言", icon: faCode },
    { id: "framework", label: "框架", icon: faPalette },
    { id: "tool", label: "工具", icon: faTools },
  ];

  const totalSkills = skillList.length;
  const filteredSkillsCount = filteredCategories.reduce((acc, cat) => acc + cat.contents.length, 0);

  return (
    <>
      <motion.div
        variants={pageVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="font-bold text-neutral-800 dark:text-white w-full pb-10"
      >
        {/* Header */}
        <motion.div variants={sectionVariants} className="mb-6 flex text-3xl gap-3 font-bold items-center">
          <div className="bg-slate-800 dark:bg-white h-[36px] w-2 rounded"></div>
          <h2>💡 我的技能</h2>
        </motion.div>

        <motion.p variants={sectionVariants} className="text-slate-600 dark:text-slate-300 font-normal mb-6">
          展示我用于构建项目的技术和工具。共 <span className="font-bold text-slate-800 dark:text-white">{totalSkills}</span> 项技能 🚀
        </motion.p>

        {/* Search & Filter */}
        <motion.div variants={sectionVariants} className="mb-6 space-y-4">
          {/* Search bar */}
          <div className="relative">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
            />
            <input
              type="text"
              placeholder="搜索技能..."
              className="w-full md:w-1/2 pl-11 pr-10 py-3 rounded-xl border border-slate-200 bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-white shadow-sm focus:ring-2 focus:ring-slate-400 focus:border-slate-400 focus:outline-none transition"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
          </div>

          {/* Filter buttons */}
          <div className="flex flex-wrap gap-2">
            {filterButtons.map((btn) => (
              <button
                key={btn.id}
                onClick={() => setActiveFilter(btn.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition flex items-center gap-2 ${activeFilter === btn.id
                  ? "bg-slate-800 text-white dark:bg-white dark:text-slate-900"
                  : "bg-slate-100 text-slate-600 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                  }`}
              >
                {btn.icon && <FontAwesomeIcon icon={btn.icon} className="text-xs" />}
                {btn.label}
              </button>
            ))}
          </div>

          {/* Results count */}
          {searchQuery && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              找到 <span className="font-bold text-slate-700 dark:text-slate-200">{filteredSkillsCount}</span> 项结果
            </p>
          )}
        </motion.div>

        {/* Categories */}
        <motion.div variants={sectionVariants} className="space-y-4">
          {filteredCategories.map((category, index) => {
            const isOpen = searchQuery ? true : openCategory === index;
            const skillCount = category.contents.length;

            if (skillCount === 0) return null;

            return (
              <div
                key={index}
                className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition"
              >
                {/* Category Header */}
                <div
                  className="cursor-pointer flex justify-between items-center py-4 px-5 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                  onClick={() => toggleCategory(index)}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{category.icon}</span>
                    <h2 className="text-lg font-bold text-slate-800 dark:text-white">{category.name}</h2>
                    <span className="text-xs font-bold text-white bg-slate-700 dark:bg-slate-600 px-2 py-0.5 rounded-full">
                      {skillCount}
                    </span>
                  </div>
                  <FontAwesomeIcon
                    icon={isOpen ? faChevronUp : faChevronDown}
                    className="text-slate-500"
                  />
                </div>

                {/* Category Content */}
                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      variants={containerVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      className="grid md:grid-cols-2 gap-4 p-5 pt-0 overflow-hidden"
                    >
                      {category.contents.map((skill, idx) => (
                        <SkillCard key={idx} skill={skill} searchQuery={searchQuery} />
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}

          {/* No results */}
          {filteredSkillsCount === 0 && (
            <div className="text-center py-12 text-slate-500">
              <p className="text-lg">未找到匹配的技能 🔎</p>
              <p className="text-sm mt-2">尝试使用其他关键词搜索</p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </>
  );
}

export default Skills;
